import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    plan: '',
    full_name: '',
    birth_date: '',
    company_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEnterpriseInfoModal, setShowEnterpriseInfoModal] = useState(false);
  const navigate = useNavigate();
  const isRoleSelectDisabled = !formData.plan || formData.plan === 'TRIAL' || formData.plan === 'STANDARD';

  // Rótulos amigáveis para resumo de erros
  const fieldLabels = {
    plan: 'Plano',
    full_name: 'Nome Completo',
    birth_date: 'Data de Nascimento',
    company_name: 'Nome da Empresa',
    email: 'E-mail',
    password: 'Senha',
    confirmPassword: 'Confirmar Senha',
    role: 'Perfil'
  };

  // Função para validar nome completo
  const validateFullName = (name) => {
    if (!name || !name.trim()) {
      return 'Preencher Campo';
    }
    if (name.trim().length < 3) {
      return 'Nome completo deve ter pelo menos 3 caracteres';
    }
    if (name.trim().length > 100) {
      return 'Nome completo deve ter no máximo 100 caracteres';
    }
    if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(name.trim())) {
      return 'Nome completo deve conter apenas caracteres alfabéticos';
    }
    return '';
  };

  // Função para validar data de nascimento
  const validateBirthDate = (date) => {
    if (!date) {
      return 'Preencher Campo';
    }
    
    const birthDate = new Date(date);
    const today = new Date();
    
    // Validar se a data é válida (não aceita datas como 31/02/2000)
    if (isNaN(birthDate.getTime()) || birthDate.toISOString().split('T')[0] !== date) {
      return 'Data de nascimento inválida';
    }
    
    // Validar se a data não é no futuro
    if (birthDate > today) {
      return 'Data de nascimento não pode ser no futuro';
    }
    
    // Calcular idade
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Validar idade mínima
    if (age < 18) {
      return 'Idade mínima é de 18 anos';
    }
    
    // Validar idade máxima (120 anos é um limite realista)
    if (age > 120) {
      return 'Idade máxima é de 120 anos';
    }
    
    return '';
  };

  // Função para validar nome da empresa
  const validateCompanyName = (name) => {
    if (!name || !name.trim()) {
      return 'Preencher Campo';
    }
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      return 'Nome da empresa deve ter pelo menos 2 caracteres';
    }
    if (trimmed.length > 150) {
      return 'Nome da empresa deve ter no máximo 150 caracteres';
    }
    if (!/^[a-zA-Z0-9À-ÿ\s]+$/.test(trimmed)) {
      return 'Nome da empresa deve conter apenas letras e números';
    }
    return '';
  };

  // Função para validar email
  const validateEmail = (email) => {
    if (!email) {
      return 'Preencher Campo';
    }
    // Limite de 100 caracteres (após trim)
    if (email.trim().length > 100) {
      return 'Email deve ter no máximo 100 caracteres';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Email deve estar em formato válido (exemplo@dominio.com)';
    }
    return '';
  };

  // Função para validar senha
  const validatePassword = (password) => {
    if (!password) {
      return 'Preencher Campo';
    }
    if (password.length < 8 || password.length > 20) {
      return 'Senha deve ter entre 8 e 20 caracteres';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Senha deve conter pelo menos uma letra minúscula';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Senha deve conter pelo menos uma letra maiúscula';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Senha deve conter pelo menos um número';
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      return 'Senha deve conter pelo menos um caractere especial (@$!%*?&)';
    }
    return '';
  };

  // Função para validar perfil
  const validateRole = (role) => {
    if (!role) {
      return 'Preencher Campo';
    }
    if (!['ADM', 'DEV', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO'].includes(role)) {
      return 'Perfil deve ser uma das opções válidas';
    }
    return '';
  };

  const validatePlan = (plan) => {
    if (!plan) {
      return 'Preencher Campo';
    }
    if (!['TRIAL', 'STANDARD', 'ENTERPRISE'].includes(plan)) {
      return 'Plano deve ser uma das opções válidas';
    }
    return '';
  };

  const validateRoleForPlan = (plan, role) => {
    if (!role) return 'Preencher Campo';

    if (plan === 'TRIAL' && role !== 'TRIAL') {
      return 'Plano Trial permite apenas perfil TRIAL';
    }
    if (plan === 'STANDARD' && role !== 'UNITARIO') {
      return 'Plano Standard permite apenas perfil UNITARIO';
    }
    if (plan === 'ENTERPRISE' && role !== 'GERENCIAL') {
      return 'No primeiro cadastro Enterprise, o perfil deve ser GERENCIAL';
    }

    return validateRole(role);
  };

  // Função para validar confirmação de senha
  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) {
      return 'Preencher Campo';
    }
    if (password !== confirmPassword) {
      return 'As senhas não coincidem';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'plan') {
      const shouldShowEnterpriseInfo = value === 'ENTERPRISE' && formData.plan !== 'ENTERPRISE';
      setFormData((prev) => {
        let nextRole = prev.role;
        let nextCompanyName = prev.company_name;

        if (value === 'TRIAL') {
          nextRole = 'TRIAL';
          nextCompanyName = '';
        } else if (value === 'STANDARD') {
          nextRole = 'UNITARIO';
          nextCompanyName = '';
        } else if (value === 'ENTERPRISE') {
          nextRole = 'GERENCIAL';
        } else {
          nextRole = '';
        }

        return {
          ...prev,
          plan: value,
          role: nextRole,
          company_name: nextCompanyName
        };
      });
      if (shouldShowEnterpriseInfo) {
        setShowEnterpriseInfoModal(true);
      }

      setErrors((prev) => ({
        ...prev,
        plan: '',
        role: '',
        company_name: ''
      }));
      return;
    }

    setFormData({
      ...formData,
      [name]: value
    });
    
    // Limpar erro específico do campo ao digitar
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleCompanyBlur = async () => {
    if (formData.role === 'TRIAL' || formData.role === 'UNITARIO') return;
    const raw = String(formData.company_name || '');
    const trimmed = raw.trim();

    if (!trimmed) return;

    const validationError = validateCompanyName(trimmed);
    if (validationError) return;

    try {
      const response = await fetch(
        getApiUrl(`auth/companies/exists?name=${encodeURIComponent(trimmed)}`),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!data?.success) return;

      if (!data.exists) {
        setErrors(prev => ({
          ...prev,
          company_name: data.error || 'Empresa não localizada. Solicite ao administrador o cadastro da empresa antes do seu cadastro.'
        }));
        return;
      }

      if (data.company?.name) {
        setFormData(prev => {
          const current = String(prev.company_name || '').trim();
          if (current !== trimmed) return prev;
          return {
            ...prev,
            company_name: data.company.name
          };
        });
      }

      setErrors(prev => {
        if (!prev.company_name) return prev;
        return { ...prev, company_name: '' };
      });
    } catch (_) {
      return;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const isSelfService = formData.role === 'TRIAL' || formData.role === 'UNITARIO';
    
    newErrors.plan = validatePlan(formData.plan);
    newErrors.full_name = validateFullName(formData.full_name);
    newErrors.birth_date = validateBirthDate(formData.birth_date);
    if (!isSelfService) {
      newErrors.company_name = validateCompanyName(formData.company_name);
    }
    newErrors.email = validateEmail(formData.email);
    newErrors.password = validatePassword(formData.password);
    newErrors.role = validateRoleForPlan(formData.plan, formData.role);
    
    newErrors.confirmPassword = validateConfirmPassword(formData.password, formData.confirmPassword);
    
    // Filtrar erros vazios
    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, value]) => value !== '')
    );
    
    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validar formulário
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Separar nome completo em first_name e last_name
      const nameParts = formData.full_name.trim().split(' ');
      const first_name = nameParts[0];
      const last_name = nameParts.slice(1).join(' ') || nameParts[0];

      const response = await fetch(getApiUrl('auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.full_name.trim(),
          first_name,
          last_name,
          birth_date: formData.birth_date,
          company_name: (formData.role === 'TRIAL' || formData.role === 'UNITARIO') ? '' : formData.company_name.trim(),
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          role: formData.role
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (formData.role === 'TRIAL') {
          navigate('/login');
        } else {
          // Redirecionar para tela de aguardo de liberação
          navigate('/aguardo-liberacao', { 
            state: { 
              email: formData.email,
              name: formData.full_name 
            } 
          });
        }
      } else {
        if (data.code === 'COMPANY_NOT_FOUND') {
          setErrors({ company_name: data.error });
        } else if (data.error.includes('empresa') || data.error.includes('Empresa')) {
          setErrors({ company_name: data.error });
        } else if (data.error.includes('email')) {
          setErrors({ email: data.error });
        } else {
          setErrors({ general: data.error || 'Erro ao criar conta' });
        }
      }
    } catch (error) {
      console.error('Erro no registro:', error);
      setErrors({ general: 'Erro de conexão. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1>📝 Cadastro de Usuário</h1>
          <p>Preencha os dados para criar sua conta</p>
        </div>

      <form onSubmit={handleSubmit} className="register-form" noValidate>
          {/* Resumo de erros múltiplos */}
          {Object.keys(errors).filter(k => k !== 'general').length > 1 && (
            <div className="error-summary" role="alert" aria-live="polite">
              <strong>Corrija os seguintes campos:</strong>
              <ul>
                {Object.entries(errors)
                  .filter(([key, value]) => key !== 'general' && value)
                  .map(([key, value]) => (
                    <li key={key}>{(fieldLabels[key] || key)}: {value}</li>
                  ))}
              </ul>
            </div>
          )}
          {/* Plano */}
          <div className="form-group">
            <label htmlFor="plan">
              Plano <span className="required">*</span>
            </label>
            <select
              id="plan"
              name="plan"
              value={formData.plan}
              onChange={handleChange}
              required
              className={errors.plan ? 'error' : ''}
            >
              <option value="">Selecione um plano</option>
              <option value="TRIAL">🕒 Trial - Avaliação (15 dias)</option>
              <option value="STANDARD">👤 Standard - Unitário (Pessoa Física)</option>
              <option value="ENTERPRISE">🏢 Enterprise - Equipe</option>
            </select>
            {errors.plan && <span className="error-text">{errors.plan}</span>}
          </div>

          {/* Perfil */}
          <div className="form-group">
            <label htmlFor="role">
              Perfil <span className="required">*</span>
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              disabled={isRoleSelectDisabled}
              className={errors.role ? 'error' : ''}
            >
              <option value="">
                {formData.plan ? 'Selecione um perfil' : 'Selecione um plano primeiro'}
              </option>
              {formData.plan === 'ENTERPRISE' && (
                <>
                  <option value="GERENCIAL">📊 GERENCIAL - Gerencial</option>
                </>
              )}
              {formData.plan === 'TRIAL' && (
                <option value="TRIAL">🕒 TRIAL - Avaliação (15 dias)</option>
              )}
              {formData.plan === 'STANDARD' && (
                <option value="UNITARIO">👤 UNITARIO - Pessoa Física</option>
              )}
            </select>
            {errors.role && <span className="error-text">{errors.role}</span>}
          </div>

          {/* Nome Completo */}
          <div className="form-group">
            <label htmlFor="full_name">
              Nome Completo <span className="required">*</span>
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              maxLength={100}
              onKeyDown={(e) => {
                // Permitir teclas de controle/navegação
                const allowed = [
                  'Tab', 'Escape', 'Backspace', 'Delete',
                  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                  'Home', 'End', 'Enter',
                  // Modificadoras e composição
                  'Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Dead'
                ];
                if (allowed.includes(e.key)) return;
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const willLen = cur.length - (selEnd - selStart) + 1; // 1 char
                // Permitir espaço
                if (e.key === ' ') {
                  if (willLen > 100) e.preventDefault();
                  return;
                }
                // Permitir apenas caráter alfabético (inclui acentos) e respeitar limite
                if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) {
                  if (willLen > 100) e.preventDefault();
                  return;
                }
                // Bloquear números e símbolos
                e.preventDefault();
              }}
              onBeforeInput={(e) => {
                // Permitir operações de deleção e comandos sem dados
                if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                // Permitir apenas letras e espaços (inclui acentos)
                if (/^[\p{L}\s]+$/u.test(e.data)) {
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + e.data.length;
                  if (willLen > 100) e.preventDefault();
                  return;
                }
                e.preventDefault();
              }}
              onPaste={(e) => {
                const text = e.clipboardData.getData('text');
                // Permitir colagem apenas com letras e espaços e respeitar limite
                if (!/^[\p{L}\s]+$/u.test(text)) {
                  e.preventDefault();
                  return;
                }
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const willLen = cur.length - (selEnd - selStart) + text.length;
                if (willLen > 100) e.preventDefault();
              }}
              onDrop={(e) => e.preventDefault()}
              pattern="^[A-Za-zÀ-ÿ\\s]+$"
              title="Use apenas letras (A–Z, á–ú) e espaços"
              required
              placeholder="Digite seu nome completo"
              className={errors.full_name ? 'error' : ''}
            />
            <div
              className={formData.full_name.length >= 100 ? 'char-counter limit' : 'char-counter'}
              aria-live="polite"
            >
              {formData.full_name.length}/100
            </div>
            {errors.full_name && <span className="error-text">{errors.full_name}</span>}
          </div>

          {/* Data de Nascimento */}
          <div className="form-group">
            <label htmlFor="birth_date">
              Data de Nascimento <span className="required">*</span>
            </label>
            <input
              type="date"
              id="birth_date"
              data-testid="register-birth-date"
              name="birth_date"
              value={formData.birth_date}
              onChange={handleChange}
              required
              className={errors.birth_date ? 'error' : ''}
            />
            {errors.birth_date && <span className="error-text">{errors.birth_date}</span>}
          </div>

          {/* Nome da Empresa (Opcional) */}
          <div className="form-group">
            <label htmlFor="company_name">
              Nome da Empresa {(formData.role !== 'TRIAL' && formData.role !== 'UNITARIO') && <span className="required">*</span>}
            </label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              onBlur={handleCompanyBlur}
              maxLength={150}
              onKeyDown={(e) => {
                const allowed = [
                  'Tab', 'Escape', 'Backspace', 'Delete',
                  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                  'Home', 'End', 'Enter',
                  'Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Dead'
                ];
                if (allowed.includes(e.key)) return;
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const willLen = cur.length - (selEnd - selStart) + 1; // 1 char
                // Permitir espaço
                if (e.key === ' ') {
                  if (willLen > 150) e.preventDefault();
                  return;
                }
                // Permitir letras e números (inclui acentos)
                if (e.key.length === 1 && /^[\p{L}\p{N}]$/u.test(e.key)) {
                  if (willLen > 150) e.preventDefault();
                  return;
                }
                // Bloquear símbolos
                e.preventDefault();
              }}
              onBeforeInput={(e) => {
                // Permitir deleções e comandos sem dados
                if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                // Permitir apenas letras, números e espaços
                if (/^[\p{L}\p{N}\s]+$/u.test(e.data)) {
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + e.data.length;
                  if (willLen > 150) e.preventDefault();
                  return;
                }
                e.preventDefault();
              }}
              onPaste={(e) => {
                const text = e.clipboardData.getData('text');
                // Permitir colagem com letras, números e espaços e respeitar limite
                if (!/^[\p{L}\p{N}\s]+$/u.test(text)) {
                  e.preventDefault();
                  return;
                }
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const willLen = cur.length - (selEnd - selStart) + text.length;
                if (willLen > 150) e.preventDefault();
              }}
              onDrop={(e) => e.preventDefault()}
              pattern="^[A-Za-z0-9À-ÿ\\s]+$"
              title="Use apenas letras, números e espaços"
              required={formData.role !== 'TRIAL' && formData.role !== 'UNITARIO'}
              disabled={formData.role === 'TRIAL' || formData.role === 'UNITARIO'}
              placeholder={(formData.role === 'TRIAL' || formData.role === 'UNITARIO') ? 'Neste plano, a empresa é criada automaticamente' : 'Digite o nome da empresa'}
              className={errors.company_name ? 'error' : ''}
            />
            <div className={`char-counter ${(formData.company_name?.length ?? 0) >= 150 ? 'limit' : ''}`}>
              {formData.company_name?.length ?? 0}/150
            </div>
            {errors.company_name && <span className="error-text">{errors.company_name}</span>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">
              E-mail <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              maxLength={100}
              onKeyDown={(e) => {
                const allowed = [
                  'Backspace', 'Delete', 'Tab', 'Escape',
                  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                  'Home', 'End', 'Enter',
                  // Modificadoras e composição
                  'Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Dead'
                ];
                if (allowed.includes(e.key)) return;
                // Para inserção de 1 caractere, respeitar limite
                if (e.key && e.key.length === 1) {
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + 1; // 1 char
                  if (willLen > 100) e.preventDefault();
                }
              }}
              onBeforeInput={(e) => {
                // Permitir operações de deleção e comandos sem dados
                if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const dataLen = e.data ? e.data.length : 0;
                const willLen = cur.length - (selEnd - selStart) + dataLen;
                if (willLen > 100) e.preventDefault();
              }}
              onPaste={(e) => {
                const text = e.clipboardData.getData('text');
                const el = e.target;
                const cur = el.value || '';
                const selStart = el.selectionStart ?? cur.length;
                const selEnd = el.selectionEnd ?? cur.length;
                const willLen = cur.length - (selEnd - selStart) + text.length;
                if (willLen > 100) e.preventDefault();
              }}
              onDrop={(e) => e.preventDefault()}
              required
              placeholder="exemplo@dominio.com"
              className={errors.email ? 'error' : ''}
            />
            <div
              className={formData.email.length >= 100 ? 'char-counter limit' : 'char-counter'}
              aria-live="polite"
            >
              {formData.email.length}/100
            </div>
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          {/* Senha */}
          <div className="form-group">
            <label htmlFor="password">
              Senha <span className="required">*</span>
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                maxLength={20}
                onKeyDown={(e) => {
                  const allowed = [
                    'Backspace', 'Delete', 'Tab', 'Escape',
                    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                    'Home', 'End', 'Enter',
                    // Modificadoras e composição
                    'Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Dead'
                  ];
                  if (allowed.includes(e.key)) return;
                  // Para inserção de 1 caractere, respeitar limite de 20
                  if (e.key && e.key.length === 1) {
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + 1; // 1 char
                    if (willLen > 20) e.preventDefault();
                  }
                }}
                onBeforeInput={(e) => {
                  // Permitir deleções e comandos sem dados
                  if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const dataLen = e.data ? e.data.length : 0;
                  const willLen = cur.length - (selEnd - selStart) + dataLen;
                  if (willLen > 20) e.preventDefault();
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + text.length;
                  if (willLen > 20) e.preventDefault();
                }}
                onDrop={(e) => e.preventDefault()}
                required
                placeholder="Mínimo 8 caracteres"
                className={errors.password ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <div
              className={formData.password.length >= 20 ? 'char-counter limit' : 'char-counter'}
              aria-live="polite"
            >
              {formData.password.length}/20
            </div>
            {errors.password && <span className="error-text">{errors.password}</span>}
            <div className="password-requirements">
              <small>
                A senha deve conter: 8-20 caracteres, 1 maiúscula, 1 minúscula, 1 número e 1 caractere especial (@$!%*?&)
              </small>
            </div>
          </div>

          {/* Confirmar Senha */}
          <div className="form-group">
            <label htmlFor="confirmPassword">
              Confirmar Senha <span className="required">*</span>
            </label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                maxLength={20}
                onKeyDown={(e) => {
                  const allowed = [
                    'Backspace', 'Delete', 'Tab', 'Escape',
                    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                    'Home', 'End', 'Enter',
                    // Modificadoras e composição
                    'Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Dead'
                  ];
                  if (allowed.includes(e.key)) return;
                  // Para inserção de 1 caractere, respeitar limite de 20
                  if (e.key && e.key.length === 1) {
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + 1; // 1 char
                    if (willLen > 20) e.preventDefault();
                  }
                }}
                onBeforeInput={(e) => {
                  // Permitir deleções e comandos sem dados
                  if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const dataLen = e.data ? e.data.length : 0;
                  const willLen = cur.length - (selEnd - selStart) + dataLen;
                  if (willLen > 20) e.preventDefault();
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  const el = e.target;
                  const cur = el.value || '';
                  const selStart = el.selectionStart ?? cur.length;
                  const selEnd = el.selectionEnd ?? cur.length;
                  const willLen = cur.length - (selEnd - selStart) + text.length;
                  if (willLen > 20) e.preventDefault();
                }}
                onDrop={(e) => e.preventDefault()}
                required
                placeholder="Confirme sua senha"
                className={errors.confirmPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Ocultar confirmação" : "Mostrar confirmação"}
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <div
              className={(formData.confirmPassword?.length ?? 0) >= 20 ? 'char-counter limit' : 'char-counter'}
              aria-live="polite"
            >
              {(formData.confirmPassword?.length ?? 0)}/20
            </div>
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>

          {/* Erro Geral */}
          {errors.general && <div className="error-message">{errors.general}</div>}

          <button 
            type="submit" 
            className="register-button"
            disabled={loading}
          >
            {loading ? '⏳ Criando conta...' : '📝 Cadastrar'}
          </button>
        </form>

        <div className="register-footer">
          <p>
            Já tem uma conta? 
            <Link to="/login" className="login-link"> Fazer login</Link>
          </p>
        </div>

        {showEnterpriseInfoModal && (
          <div className="enterprise-info-overlay" onClick={() => setShowEnterpriseInfoModal(false)}>
            <div className="enterprise-info-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Plano Enterprise selecionado</h3>
              <p>
                No primeiro cadastro, o perfil deve ser <strong>GERENCIAL</strong> (PO ou responsável).
              </p>
              <p>
                Após concluir este cadastro e entrar no sistema, vá em <strong>Usuários e Funções</strong>
                {' '}para criar os demais usuários da equipe.
              </p>
              <div className="enterprise-info-actions">
                <button
                  type="button"
                  className="register-button enterprise-info-btn"
                  onClick={() => setShowEnterpriseInfoModal(false)}
                >
                  Entendi
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
